// src/components/contacts/AffiliationRelationship.tsx
//
// Shared presentation + editing for a Contact <-> Company relationship's
// details: role title at the company, relationship type, start/end dates, the
// derived Current/Former state and the Primary flag.
//
// Used from BOTH sides (Contact detail → Companies, Company detail → Contacts)
// so the two surfaces describe one relationship in one vocabulary.
//
// CURRENT / FORMER comes from the server (`isCurrent`, derived from endDate,
// never stored). The dialog only PREDICTS whether an edit ends the primary so
// it can ask for a successor up front; the server's 409
// PRIMARY_MUST_BE_CURRENT is authoritative and also reveals the choice.

import { useId, useState } from 'react';
import Modal from '../ui/Modal';
import {
  RELATIONSHIP_TYPE_OPTIONS,
  affiliationErrorMessage,
  relationshipFieldErrors,
  relationshipTypeLabel,
  type AffiliationRelationshipFields,
  type AffiliationRelationshipType,
  type CreateLinkDetails,
} from '../../api/contactCompanyApi';

/** Replacement choice meaning "leave this contact with no primary company". */
export const NO_PRIMARY = '__no_primary__';

// ── Formatting ─────────────────────────────────────────────────────────────

/** `YYYY-MM-DD` → "Mar 1, 2021". UTC so a date never shifts by a day. */
export function formatRelationshipDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** "Since Mar 1, 2021" / "Mar 1, 2021 – Jun 30, 2022" / "Until …" / null. */
export function relationshipDateRange(startDate: string | null, endDate: string | null): string | null {
  if (startDate && endDate) {
    return `${formatRelationshipDate(startDate)} – ${formatRelationshipDate(endDate)}`;
  }
  if (startDate) return `Since ${formatRelationshipDate(startDate)}`;
  if (endDate) return `Until ${formatRelationshipDate(endDate)}`;
  return null;
}

/** Today in the viewer's calendar, `YYYY-MM-DD` — only for predicting the server's rule. */
export function localTodayIso(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

// ── Display ────────────────────────────────────────────────────────────────

const BADGE = 'inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 border whitespace-nowrap';

export function RelationshipBadges({
  isPrimary,
  isCurrent,
}: Pick<AffiliationRelationshipFields, 'isPrimary' | 'isCurrent'>) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {isPrimary && (
        <span className={`${BADGE} bg-blue-50 text-blue-700 border-blue-200`} title="Primary company">
          Primary
        </span>
      )}
      {isCurrent ? (
        <span className={`${BADGE} bg-green-50 text-green-700 border-green-200`}>Current</span>
      ) : (
        <span className={`${BADGE} bg-gray-100 text-gray-600 border-gray-300`}>Former</span>
      )}
    </span>
  );
}

/** "Advisor · Since Mar 1, 2021" — type and dates, whichever exist. */
export function relationshipSummary(
  aff: Pick<AffiliationRelationshipFields, 'relationshipType' | 'startDate' | 'endDate'>,
): string | null {
  const parts = [
    relationshipTypeLabel(aff.relationshipType),
    relationshipDateRange(aff.startDate, aff.endDate),
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

// ── Edit dialog ────────────────────────────────────────────────────────────

export interface RelationshipEdit {
  roleTitle: string | null;
  relationshipType: AffiliationRelationshipType | null;
  startDate: string | null;
  endDate: string | null;
  isPrimary?: true;
  replacementPrimaryId?: string;
  allowNoPrimary?: true;
}

export interface ReplacementCandidate {
  affiliationId: string;
  name: string;
}

const INPUT =
  'w-full min-w-0 border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/**
 * Accessible edit dialog for one relationship. Every input has a visible
 * label; a field error is announced via `aria-invalid` + `aria-describedby`.
 *
 * `replacementCandidates` are the contact's OTHER current affiliations the
 * caller is allowed to see. When ending the primary, the user picks one of
 * them or explicitly chooses "No primary company".
 */
export function EditRelationshipDialog({
  title,
  affiliation,
  replacementCandidates,
  onSave,
  onClose,
}: {
  title: string;
  affiliation: AffiliationRelationshipFields;
  replacementCandidates: ReplacementCandidate[];
  /** Resolve on success; reject with the RTK error to show it here. */
  onSave: (edit: RelationshipEdit) => Promise<void>;
  onClose: () => void;
}) {
  const uid = useId();
  const [roleTitle, setRoleTitle] = useState(affiliation.roleTitle ?? '');
  const [relationshipType, setRelationshipType] = useState<string>(affiliation.relationshipType ?? '');
  const [startDate, setStartDate] = useState(affiliation.startDate ?? '');
  const [endDate, setEndDate] = useState(affiliation.endDate ?? '');
  const [makePrimary, setMakePrimary] = useState(false);
  const [successor, setSuccessor] = useState('');
  const [serverAsksSuccessor, setServerAsksSuccessor] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const endsInPast = Boolean(endDate) && endDate < localTodayIso();
  const localOrderError =
    startDate && endDate && endDate < startDate ? 'End date cannot be before the start date' : null;
  const endingPrimary = affiliation.isPrimary && (endsInPast || serverAsksSuccessor);
  const canOfferPrimary = !affiliation.isPrimary && !endsInPast;
  const errors: Record<string, string> = {
    ...fieldErrors,
    ...(localOrderError ? { endDate: localOrderError } : {}),
  };

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (localOrderError) return;
    if (endingPrimary && !successor) {
      setFormError('Choose which company becomes primary, or none.');
      return;
    }
    setSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      await onSave({
        roleTitle: roleTitle.trim() || null,
        relationshipType: (relationshipType || null) as AffiliationRelationshipType | null,
        startDate: startDate || null,
        endDate: endDate || null,
        ...(makePrimary && canOfferPrimary ? { isPrimary: true as const } : {}),
        ...(endingPrimary && successor === NO_PRIMARY ? { allowNoPrimary: true as const } : {}),
        ...(endingPrimary && successor && successor !== NO_PRIMARY
          ? { replacementPrimaryId: successor }
          : {}),
      });
    } catch (err) {
      const code = (err as { data?: { code?: string } })?.data?.code;
      if (code === 'PRIMARY_MUST_BE_CURRENT' && affiliation.isPrimary) setServerAsksSuccessor(true);
      setFieldErrors(relationshipFieldErrors(err));
      setFormError(affiliationErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  const describe = (field: string) =>
    errors[field] ? { 'aria-invalid': true as const, 'aria-describedby': `${uid}-${field}-err` } : {};
  const fieldError = (field: string) =>
    errors[field] ? (
      <p id={`${uid}-${field}-err`} className="mt-1 text-xs text-red-600">
        {errors[field]}
      </p>
    ) : null;
  const border = (field: string) => (errors[field] ? 'border-red-400' : 'border-gray-300');

  return (
    <Modal isOpen onClose={onClose} title={title} size="md">
      <form onSubmit={submit} className="space-y-3" noValidate>
        <div>
          <label htmlFor={`${uid}-role`} className="block text-sm font-medium text-gray-700 mb-1">
            Role at this company
          </label>
          <input
            id={`${uid}-role`}
            value={roleTitle}
            onChange={(e) => setRoleTitle(e.target.value)}
            placeholder="e.g. CFO"
            maxLength={255}
            className={`${INPUT} ${border('roleTitle')}`}
            {...describe('roleTitle')}
          />
          {fieldError('roleTitle')}
        </div>

        <div>
          <label htmlFor={`${uid}-type`} className="block text-sm font-medium text-gray-700 mb-1">
            Relationship type
          </label>
          <select
            id={`${uid}-type`}
            value={relationshipType}
            onChange={(e) => setRelationshipType(e.target.value)}
            className={`${INPUT} ${border('relationshipType')} bg-white`}
            {...describe('relationshipType')}
          >
            <option value="">Not specified</option>
            {RELATIONSHIP_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {fieldError('relationshipType')}
        </div>

        <div className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2">
          <div className="min-w-0">
            <label htmlFor={`${uid}-start`} className="block text-sm font-medium text-gray-700 mb-1">
              Start date
            </label>
            <input
              id={`${uid}-start`}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={`${INPUT} ${border('startDate')}`}
              {...describe('startDate')}
            />
            {fieldError('startDate')}
          </div>
          <div className="min-w-0">
            <label htmlFor={`${uid}-end`} className="block text-sm font-medium text-gray-700 mb-1">
              End date
            </label>
            <input
              id={`${uid}-end`}
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
              className={`${INPUT} ${border('endDate')}`}
              {...describe('endDate')}
            />
            {fieldError('endDate')}
          </div>
        </div>
        <p className="text-xs text-gray-500">Leave the end date empty while the relationship is ongoing.</p>

        {canOfferPrimary && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={makePrimary} onChange={(e) => setMakePrimary(e.target.checked)} />
            Make this the primary company
          </label>
        )}

        {endingPrimary && (
          <div className="space-y-1.5 rounded border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-900">
              This is the primary company and the relationship has ended. A primary company must be
              current — choose which company becomes primary, or none.
            </p>
            <label htmlFor={`${uid}-successor`} className="sr-only">
              New primary company
            </label>
            <select
              id={`${uid}-successor`}
              aria-label="New primary company"
              value={successor}
              onChange={(e) => setSuccessor(e.target.value)}
              className={`${INPUT} border-gray-300 bg-white`}
            >
              <option value="">Select…</option>
              {replacementCandidates.map((c) => (
                <option key={c.affiliationId} value={c.affiliationId}>
                  {c.name}
                </option>
              ))}
              <option value={NO_PRIMARY}>No primary company</option>
            </select>
          </div>
        )}

        {formError && (
          <p role="alert" className="text-sm text-red-600">
            {formError}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-gray-600">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || Boolean(localOrderError)}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Create-with-links: optional per-link details ───────────────────────────

/**
 * A collapsed "Relationship details (optional)" section for create forms.
 * One compact row per selected link: role, type, start date. Nothing here is
 * required — creating stays a one-step action when the section stays closed.
 */
export function LinkDetailsSection({
  links,
  value,
  onChange,
  errors = {},
  disabled,
}: {
  /** The selected records on the OTHER end, by id + display name. */
  links: Array<{ id: string; name: string }>;
  value: Record<string, CreateLinkDetails>;
  onChange: (next: Record<string, CreateLinkDetails>) => void;
  /** Server field errors keyed `<id>.<field>`. */
  errors?: Record<string, string>;
  disabled?: boolean;
}) {
  const uid = useId();
  if (links.length === 0) return null;
  const hasErrors = Object.keys(errors).length > 0;

  const set = (id: string, patch: CreateLinkDetails) =>
    onChange({ ...value, [id]: { ...value[id], ...patch } });

  return (
    <details className="rounded border border-gray-200" open={hasErrors || undefined}>
      <summary className="cursor-pointer select-none px-3 py-2 text-sm text-gray-700">
        Relationship details <span className="text-gray-500">(optional)</span>
      </summary>
      <div className="space-y-3 px-3 pb-3">
        {links.map((link, i) => {
          const v = value[link.id] ?? {};
          const err = (f: string) => errors[`${link.id}.${f}`];
          const id = (f: string) => `${uid}-${i}-${f}`;
          const aria = (f: string) =>
            err(f) ? { 'aria-invalid': true as const, 'aria-describedby': `${id(f)}-err` } : {};
          return (
            <fieldset key={link.id} className="min-w-0 space-y-2 border-t border-gray-100 pt-2 first:border-t-0">
              <legend className="text-sm font-medium text-gray-800 truncate max-w-full">{link.name}</legend>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <div className="min-w-0">
                  <label htmlFor={id('role')} className="block text-xs text-gray-600">
                    Role
                  </label>
                  <input
                    id={id('role')}
                    value={v.roleTitle ?? ''}
                    disabled={disabled}
                    maxLength={255}
                    onChange={(e) => set(link.id, { roleTitle: e.target.value })}
                    className={`${INPUT} border-gray-300`}
                    {...aria('roleTitle')}
                  />
                  {err('roleTitle') && (
                    <p id={`${id('roleTitle')}-err`} className="text-xs text-red-600">{err('roleTitle')}</p>
                  )}
                </div>
                <div className="min-w-0">
                  <label htmlFor={id('type')} className="block text-xs text-gray-600">
                    Type
                  </label>
                  <select
                    id={id('type')}
                    value={v.relationshipType ?? ''}
                    disabled={disabled}
                    onChange={(e) =>
                      set(link.id, {
                        relationshipType: (e.target.value || null) as AffiliationRelationshipType | null,
                      })
                    }
                    className={`${INPUT} border-gray-300 bg-white`}
                    {...aria('relationshipType')}
                  >
                    <option value="">Not specified</option>
                    {RELATIONSHIP_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {err('relationshipType') && (
                    <p id={`${id('relationshipType')}-err`} className="text-xs text-red-600">
                      {err('relationshipType')}
                    </p>
                  )}
                </div>
                <div className="min-w-0">
                  <label htmlFor={id('start')} className="block text-xs text-gray-600">
                    Start date
                  </label>
                  <input
                    id={id('start')}
                    type="date"
                    value={v.startDate ?? ''}
                    disabled={disabled}
                    onChange={(e) => set(link.id, { startDate: e.target.value || null })}
                    className={`${INPUT} border-gray-300`}
                    {...aria('startDate')}
                  />
                  {err('startDate') && (
                    <p id={`${id('startDate')}-err`} className="text-xs text-red-600">{err('startDate')}</p>
                  )}
                </div>
              </div>
            </fieldset>
          );
        })}
      </div>
    </details>
  );
}

/**
 * The details map to send: only selected links, only non-empty values.
 * Returns undefined when nothing was entered, so the request is unchanged
 * from before for anyone who never opens the section.
 */
export function compactLinkDetails(
  value: Record<string, CreateLinkDetails>,
  selectedIds: string[],
): Record<string, CreateLinkDetails> | undefined {
  const out: Record<string, CreateLinkDetails> = {};
  for (const id of selectedIds) {
    const v = value[id];
    if (!v) continue;
    const entry: CreateLinkDetails = {};
    if (v.roleTitle && v.roleTitle.trim()) entry.roleTitle = v.roleTitle.trim();
    if (v.relationshipType) entry.relationshipType = v.relationshipType;
    if (v.startDate) entry.startDate = v.startDate;
    if (Object.keys(entry).length > 0) out[id] = entry;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}
